import { KurooApiError } from "@kuroo/sdk";
import { createBookingAnalyticsEvent } from "@kuroo/contracts";
import type {
  BookingAnalyticsActor,
  BookingAnalyticsEvent,
  BookingAnalyticsEventName,
  BookingAnalyticsKind,
  BookingAnalyticsReason,
  BookingAnalyticsStep
} from "@kuroo/contracts";
import { randomIdempotency } from "./booking-utils";

export type BookingAnalyticsDetails = {
  actor?: BookingAnalyticsActor;
  step?: BookingAnalyticsStep;
  attempt?: number;
  serviceId?: string;
  date?: string;
  slotCount?: number;
  kind?: BookingAnalyticsKind;
  durationMs?: number;
  reason?: BookingAnalyticsReason;
};

export type BookingAnalyticsState = {
  flowId: string;
  startedAt: number;
  attempt: number;
  lastServiceId?: string;
  clientSelected: boolean;
  confirmed: boolean;
  cancelled: boolean;
};

export function createBookingAnalyticsState(now = Date.now()): BookingAnalyticsState {
  return {
    flowId: randomIdempotency("booking-flow"),
    startedAt: now,
    attempt: 1,
    clientSelected: false,
    confirmed: false,
    cancelled: false
  };
}

export function bookingAnalyticsEvent(
  state: BookingAnalyticsState,
  name: BookingAnalyticsEventName,
  step: BookingAnalyticsStep,
  serviceId: string | undefined,
  details: BookingAnalyticsDetails = {}
): BookingAnalyticsEvent {
  const effectiveServiceId = details.serviceId ?? serviceId;
  return createBookingAnalyticsEvent({
    name,
    flowId: state.flowId,
    actor: details.actor ?? "anonymous",
    step: details.step ?? step,
    attempt: details.attempt ?? state.attempt,
    ...(effectiveServiceId ? { serviceId: effectiveServiceId } : {}),
    ...(details.date ? { date: details.date } : {}),
    ...(details.slotCount === undefined ? {} : { slotCount: details.slotCount }),
    ...(details.kind ? { kind: details.kind } : {}),
    ...(details.durationMs === undefined ? {} : { durationMs: details.durationMs }),
    ...(details.reason ? { reason: details.reason } : {})
  });
}

export function markClientSelected(state: BookingAnalyticsState): BookingAnalyticsState | undefined {
  if (state.clientSelected) return undefined;
  return { ...state, clientSelected: true };
}

export function markServiceSelected(state: BookingAnalyticsState, serviceId: string): BookingAnalyticsState | undefined {
  if (!serviceId || state.lastServiceId === serviceId) return undefined;
  return { ...state, lastServiceId: serviceId };
}

export function markBookingConfirmed(state: BookingAnalyticsState): BookingAnalyticsState | undefined {
  if (state.confirmed) return undefined;
  return { ...state, confirmed: true };
}

export function markBookingCancelled(state: BookingAnalyticsState): BookingAnalyticsState | undefined {
  if (state.confirmed || state.cancelled) return undefined;
  return { ...state, cancelled: true };
}

export function isBookingSlotConflict(error: unknown): boolean {
  return error instanceof KurooApiError && [
    "SLOT_UNAVAILABLE",
    "SLOT_CONTEXT_INVALID",
    "SLOT_DURATION_MISMATCH",
    "HOLD_EXPIRED",
    "HOLD_MISMATCH",
    "HOLD_CAPACITY_MISMATCH",
    "QUOTE_MISMATCH",
    "QUOTE_NOT_AVAILABLE"
  ].includes(error.code);
}

export function recordBookingSlotConflict(state: BookingAnalyticsState, error: unknown): BookingAnalyticsState | undefined {
  if (!isBookingSlotConflict(error)) return undefined;
  return { ...state, attempt: state.attempt + 1 };
}

